/*
 * Widget-Type miniatures. Rendered inside the WidgetPicker card so the
 * operator can recognise a type at a glance without having to read the
 * description. Each preview is a small self-contained SVG matched by
 * widgetTypeId — no external assets, no runtime data-binding, just a
 * hint at the shape and colour of the real widget.
 *
 * When a new widget type is registered in widgets.json, either add a case
 * to `WidgetPreview` below or let it fall through to the generic tile
 * placeholder. The fallback is deliberate: forgetting to add a preview
 * should not block a widget from appearing in the picker.
 */

const ACCENT = 'var(--accent)';
const MUTED = 'var(--text-muted)';
const SUBTLE = 'var(--border-subtle)';
const PANEL = 'var(--surface-panel)';

interface Props {
  widgetTypeId: string;
}

export function WidgetPreview({ widgetTypeId }: Props) {
  return (
    <div class="hv-widget-card__preview" aria-hidden="true">
      <svg
        viewBox="0 0 120 72"
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        height="100%"
      >
        {renderPreview(widgetTypeId)}
      </svg>
    </div>
  );
}

function renderPreview(id: string) {
  switch (id) {
    case 'single-metric': return <PreviewSingleMetric />;
    case 'trend-chart':   return <PreviewTrendChart />;
    case 'multi-trend':   return <PreviewMultiTrend />;
    case 'value-table':   return <PreviewValueTable />;
    case 'alarm-list':    return <PreviewAlarmList />;
    case 'geo-map':       return <PreviewGeoMap />;
    case 'energy-flow':   return <PreviewEnergyFlow />;
    case 'text':          return <PreviewText />;
    case 'heading':       return <PreviewHeading />;
    case 'image':         return <PreviewImage />;
    case 'separator':     return <PreviewSeparator />;
    case 'spacer':        return <PreviewSpacer />;
    default:              return <PreviewGeneric />;
  }
}

function PreviewSingleMetric() {
  return (
    <g>
      <text x="10" y="34" fill={ACCENT} font-size="28" font-weight="600">21.4</text>
      <text x="72" y="34" fill={MUTED} font-size="12">°C</text>
      <path d="M10 58 Q 30 44 50 52 T 90 46 T 110 40" stroke={ACCENT} fill="none" stroke-width="1.5" opacity="0.7" />
    </g>
  );
}

function PreviewTrendChart() {
  return (
    <g>
      <path d="M6 60 L 30 44 L 46 50 L 66 32 L 88 40 L 114 22" stroke={ACCENT} fill="none" stroke-width="2" />
      <line x1="6" y1="66" x2="114" y2="66" stroke={SUBTLE} stroke-width="0.8" />
      <line x1="6" y1="6" x2="6" y2="66" stroke={SUBTLE} stroke-width="0.8" />
    </g>
  );
}

function PreviewMultiTrend() {
  return (
    <g>
      <path d="M6 58 Q 30 46 50 50 T 90 40 T 114 30" stroke={ACCENT} fill="none" stroke-width="2" />
      <path d="M6 46 Q 30 40 50 30 T 90 32 T 114 20" stroke="#38bdf8" fill="none" stroke-width="2" />
      <path d="M6 62 Q 30 58 50 62 T 90 54 T 114 50" stroke="#f59e0b" fill="none" stroke-width="2" />
      <line x1="6" y1="66" x2="114" y2="66" stroke={SUBTLE} stroke-width="0.8" />
    </g>
  );
}

function PreviewValueTable() {
  return (
    <g>
      <rect x="6" y="8" width="108" height="10" fill={PANEL} stroke={SUBTLE} />
      <rect x="6" y="20" width="108" height="10" fill="none" stroke={SUBTLE} />
      <rect x="6" y="32" width="108" height="10" fill="none" stroke={SUBTLE} />
      <rect x="6" y="44" width="108" height="10" fill="none" stroke={SUBTLE} />
      <line x1="52" y1="8"  x2="52" y2="54" stroke={SUBTLE} />
      <line x1="82" y1="8"  x2="82" y2="54" stroke={SUBTLE} />
      <circle cx="98" cy="25" r="2" fill={ACCENT} />
      <circle cx="98" cy="37" r="2" fill={ACCENT} />
      <circle cx="98" cy="49" r="2" fill={ACCENT} />
    </g>
  );
}

function PreviewAlarmList() {
  return (
    <g>
      <rect x="6"  y="10" width="108" height="12" fill="none" stroke="#ef4444" stroke-width="1.5" />
      <rect x="6"  y="26" width="108" height="12" fill="none" stroke="#f59e0b" stroke-width="1.5" />
      <rect x="6"  y="42" width="108" height="12" fill="none" stroke={SUBTLE} />
      <circle cx="14" cy="16" r="3" fill="#ef4444" />
      <circle cx="14" cy="32" r="3" fill="#f59e0b" />
      <circle cx="14" cy="48" r="3" fill={MUTED} />
    </g>
  );
}

function PreviewGeoMap() {
  return (
    <g>
      <rect x="6" y="6" width="108" height="60" fill={PANEL} stroke={SUBTLE} />
      <path d="M14 40 Q 30 24 46 34 T 78 30 T 108 38" stroke={SUBTLE} fill="none" />
      <path d="M20 56 Q 40 46 60 52 T 96 50" stroke={SUBTLE} fill="none" />
      <circle cx="42" cy="30" r="4" fill={ACCENT} stroke={PANEL} stroke-width="2" />
      <circle cx="78" cy="48" r="4" fill={ACCENT} stroke={PANEL} stroke-width="2" />
      <circle cx="60" cy="20" r="4" fill={ACCENT} stroke={PANEL} stroke-width="2" />
    </g>
  );
}

function PreviewEnergyFlow() {
  return (
    <g>
      {/* Left column: two source nodes */}
      <circle cx="14" cy="22" r="6" fill={PANEL} stroke={ACCENT} stroke-width="1.8" />
      <circle cx="14" cy="52" r="6" fill={PANEL} stroke={ACCENT} stroke-width="1.8" />
      {/* Middle node */}
      <circle cx="60" cy="36" r="6" fill={PANEL} stroke={ACCENT} stroke-width="1.8" />
      {/* Right column: two sink nodes */}
      <circle cx="106" cy="22" r="6" fill={PANEL} stroke={ACCENT} stroke-width="1.8" />
      <circle cx="106" cy="52" r="6" fill={PANEL} stroke={ACCENT} stroke-width="1.8" />
      {/* Bezier flows, thicker = larger */}
      <path d="M20 22 C 40 22 40 32 54 34" stroke={ACCENT} fill="none" stroke-width="3.5" opacity="0.85" />
      <path d="M20 52 C 40 52 40 40 54 38" stroke={ACCENT} fill="none" stroke-width="2" opacity="0.85" />
      <path d="M66 34 C 80 32 82 22 100 22" stroke={ACCENT} fill="none" stroke-width="3" opacity="0.85" />
      <path d="M66 38 C 80 42 82 52 100 52" stroke={ACCENT} fill="none" stroke-width="1.5" opacity="0.6" />
    </g>
  );
}

function PreviewText() {
  return (
    <g>
      <rect x="8"  y="12" width="70" height="4" fill={MUTED} rx="1" />
      <rect x="8"  y="22" width="100" height="3" fill={SUBTLE} rx="1" />
      <rect x="8"  y="30" width="96"  height="3" fill={SUBTLE} rx="1" />
      <rect x="8"  y="38" width="88"  height="3" fill={SUBTLE} rx="1" />
      <rect x="8"  y="50" width="60"  height="3" fill={SUBTLE} rx="1" />
    </g>
  );
}

function PreviewHeading() {
  return (
    <g>
      <rect x="10" y="26" width="86" height="8" fill={ACCENT} opacity="0.85" rx="1.5" />
      <line x1="10" y1="42" x2="110" y2="42" stroke={SUBTLE} stroke-width="1.2" />
    </g>
  );
}

function PreviewImage() {
  return (
    <g>
      <rect x="8" y="8" width="104" height="56" fill={PANEL} stroke={SUBTLE} />
      <path d="M8 54 L 40 34 L 60 46 L 84 26 L 112 52 L 112 64 L 8 64 Z" fill={ACCENT} opacity="0.35" />
      <circle cx="90" cy="20" r="6" fill={ACCENT} opacity="0.6" />
    </g>
  );
}

function PreviewSeparator() {
  return (
    <g>
      <line x1="14" y1="36" x2="106" y2="36" stroke={ACCENT} stroke-width="2" />
    </g>
  );
}

function PreviewSpacer() {
  return (
    <g>
      <rect x="14" y="14" width="92" height="44" fill="none" stroke={SUBTLE} stroke-dasharray="3 3" />
    </g>
  );
}

function PreviewGeneric() {
  return (
    <g>
      <rect x="14" y="14" width="92" height="44" fill={PANEL} stroke={SUBTLE} />
      <circle cx="60" cy="36" r="6" fill={ACCENT} opacity="0.7" />
    </g>
  );
}
